import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Container,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { useAlert } from "../../components/Alert";
import api from "../../globals/axios";
import { INITIAL_FORM_DATA, FORM_FIELDS } from "./FormFields";
import { validationSchema } from "./BiocharFormValidation";
import { renderFormField } from "./BiocharFormFields";
import { useNavigate } from "react-router-dom";
import SimpleHeader from "../../components/SimpleHeader";

const BiocharInterestsForm = () => {
  const { showAlert } = useAlert();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [cropOptions, setCropOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postOffices, setPostOffices] = useState([]);
  const [pincodeError, setPincodeError] = useState("");
  const navigate = useNavigate();

  // Fetch crop options
  const fetchCropsList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCropOptions(response.data);
    } catch (error) {
      console.error("Error Fetching Commodities list:", error);
    }
  };

  useEffect(() => {
    fetchCropsList();
  }, []);

  // Pincode change effect with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(async () => {
      if (formData.pin_code && formData.pin_code.length === 6) {
        try {
          const response = await api.get(`common/pincode/${formData.pin_code}`);
          const postOfficesData = response.data[0]?.PostOffice;

          if (postOfficesData && postOfficesData.length > 0) {
            const firstPostOffice = postOfficesData[0];
            setFormData((prev) => ({
              ...prev,
              state: firstPostOffice.State,
              district: firstPostOffice.District,
              post_office: firstPostOffice.Name,
            }));
            setPostOffices(postOfficesData);
            setPincodeError("");
          } else {
            handlePincodeError();
          }
        } catch (error) {
          console.error("Error fetching pincode data:", error);
          handlePincodeError();
        }
      } else if (formData.pin_code.length > 0) {
        setPincodeError("Pincode must be 6 digits");
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [formData.pin_code]);

  const handlePincodeError = () => {
    setPostOffices([]);
    setFormData((prev) => ({
      ...prev,
      state: "",
      district: "",
      post_office: "",
    }));
    setPincodeError("Invalid pincode or no post offices found");
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "pin_code" && value.length > 6) {
      showAlert("error", "Pincode cannot have more than 6 characters");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    try {
      // Get the validation schema for this field
      const fieldSchema = validationSchema.fields[name];
      if (fieldSchema) {
        await fieldSchema.validate(value);
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleCropChange = (event, newValue) => {
    const cropIds = newValue.map((crop) => crop.id);
    setFormData((prev) => ({ ...prev, crops: cropIds }));

    if (cropIds.length > 0) {
      setErrors((prev) => ({ ...prev, crops: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, crops: "Select at least one crop" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (formData.crops.length === 0) {
      setErrors((prev) => ({ ...prev, crops: "Select at least one crop" }));
      return;
    }

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      await api.post("/fpo/biochar-interests/", formData);

      showAlert("success", "Successfully submitted your details.");
      navigate("/success-confirmation");
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert("error", "Some error occurred while submitting the details");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Biochar Interest Form - Farmapp</title>
      </Helmet>
      <SimpleHeader />
      <Container maxWidth="md">
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
          <CustomTitle>Biochar Interest Form</CustomTitle>
          <Grid container spacing={3} mt={1}>
            {FORM_FIELDS.map((field) =>
              renderFormField({
                field,
                formData,
                errors,
                handleChange,
                handleCropChange,
                cropOptions,
                postOffices,
                pincodeError,
              })
            )}

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.post_office}>
                <InputLabel>Post Office *</InputLabel>
                <Select
                  name="post_office"
                  value={formData.post_office}
                  onChange={handleChange}
                  label="Post Office *"
                >
                  {postOffices.map((office) => (
                    <MenuItem key={office.Name} value={office.Name}>
                      {office.Name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.post_office && (
                  <FormHelperText>{errors.post_office}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={cropOptions}
                getOptionLabel={(option) => option.name}
                value={cropOptions.filter((crop) =>
                  formData.crops.includes(crop.id)
                )}
                onChange={handleCropChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Crops"
                    error={!!errors.crops}
                    helperText={errors.crops || "Select at least one crop"}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ color: "white", padding: "10px 0" }}
              >
                {isSubmitting ? "Submitting..." : "Submit Biochar Interest"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default BiocharInterestsForm;
